import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { Input, IconButton, Tooltip } from '@picsio/ui';
import { Ok, CloseIcon } from '@picsio/icons';
import {
  string, arrayOf, func, number, shape,
} from 'prop-types';

import l18n from '../../shared/strings';
import { getCollectionNameValidationError } from '../../helpers/collectionName';

const NEW_COLLECTION = 'New collection';

const AddItemForm = ({
  defaultValue, item, forbiddenNames, onSubmit, onCancel, indent,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [tooltipContent, setTooltipContent] = useState(null);
  const $input = useRef();
  const isMounted = useRef(true);
  const isButtonPressed = useRef(false);

  const applyValidation = useCallback((newValue) => {
    const charError = getCollectionNameValidationError(newValue);
    if (charError) {
      setError(true);
      setTooltipContent(charError);
      return;
    }
    const isDuplicate = !newValue || forbiddenNames.includes(newValue.toLowerCase().trim());
    setError(isDuplicate);
    setTooltipContent(isDuplicate && newValue
      ? 'An item with this name already exists in this location. Please choose another name.'
      : null);
  }, [forbiddenNames]);

  const validate = useCallback((newValue) => {
    setValue(newValue);
    applyValidation(newValue);
  }, [applyValidation]);

  /** Did mount */
  useEffect(() => {
    if (!defaultValue) {
      let newValue = NEW_COLLECTION;
      let num = 2;
      while (forbiddenNames.includes(newValue.toLowerCase()) && num < 501) {
        newValue = `${NEW_COLLECTION} ${num}`;
        num += 1;
      }
      validate(newValue);
      setTimeout(() => {
        if ($input.current) $input.current.firstChild.select();
      }, 100);
    } else {
      validate(defaultValue);
    }

    return () => {
      isMounted.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback((e) => {
    validate(e.currentTarget.value);
  }, [validate]);

  const handleCancel = useCallback((e) => {
    e?.stopPropagation?.();
    isButtonPressed.current = true;
    onCancel();
  }, [onCancel]);

  const handleSubmit = useCallback((e) => {
    e?.stopPropagation?.();
    isButtonPressed.current = true;
    if (error) return;
    onSubmit(item, value);
  }, [item, onSubmit, value, error]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!isMounted.current) return;
      if (isButtonPressed.current) {
        isButtonPressed.current = false;
        return;
      }
      onCancel();
    }, 200);
  }, [onCancel]);

  const handleKeydown = useCallback((e) => {
    e.stopPropagation();
    switch (e.keyCode) {
    /** Esc */
    case 27:
      handleCancel(e);
      break;
      /** Enter */
    case 13:
      handleSubmit();
      break;
    default:
      break;
    }
  }, [handleSubmit, handleCancel]);

  return (
    <div className="dropdownTreeItem">
      <div className="row" style={{ paddingLeft: indent }}>
        <div className="dropdownTreeAddForm">
          <Tooltip content={tooltipContent} placement="top" disabled={!tooltipContent}>
            <Input
              value={value}
              ref={$input}
              autoFocus
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeydown}
              error={error}
            />
          </Tooltip>
          <div className="dropdownTreeFormButtons leftShadow">
            <IconButton onClick={handleCancel} size="xs"><CloseIcon /></IconButton>
            <IconButton
              onClick={handleSubmit}
              disabled={error}
              size="xs"
              aria-label={error ? tooltipContent : l18n.TREE_PLACEHOLDER.createNew}
            >
              <Ok />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

AddItemForm.defaultProps = {
  indent: 0,
  onSubmit: Function.prototype,
  onCancel: Function.prototype,
  forbiddenNames: [],
  defaultValue: '',
};

AddItemForm.propTypes = {
  defaultValue: string,
  item: shape({
    _id: string,
    name: string,
    path: string,
  }).isRequired,
  forbiddenNames: arrayOf(string),
  onSubmit: func,
  onCancel: func,
  indent: number,
};

export default AddItemForm;
